import { Fixture, OptionMarket, Participant } from '@cds/betting-offer';
import { RaceEntity } from '@cds/betting-offer/domain-specific/horse-racing';
import { RacingPostFixtureData } from '@cds/betting-offer/domain-specific/horse-racing/racing-post';
import { TimeformFixtureData } from '@cds/betting-offer/domain-specific/horse-racing/timeform';
import { EventOptionGroup, EventOptionSet } from '@frontend/sports/betting-offer/feature/model';
import { Omit } from '@frontend/sports/common/core/utils/extended-types';

import { WinGameParticipant } from './racing.model';

export enum NextRaceFilters {
    All = 'all filter',
    Ukireland = 'UK & Ireland filter',
}

type ExcludeKeys = 'tournament' | 'scoreboard' | 'games' | 'groupId' | 'liveType' | 'liveAlert' | 'source';

export type OptionMarketsFixture = Omit<Fixture, 'participants' | 'games'>;

export type ParticipantsFixture = Omit<Fixture, 'optionMarkets' | 'games'>;

export type GamesFixture = Omit<Fixture, 'optionMarkets' | 'participants'>;

export interface WinGameFixture extends Omit<Fixture, ExcludeKeys> {
    fixtureId: string;
    participants: WinGameParticipant[];
    optionGroups: EventOptionGroup[];
    optionSets: EventOptionSet[];
    nextRaceFilter?: NextRaceFilters;
    isOptionBet: boolean;
    timeform?: TimeformFixtureData;
    racingpost?: RacingPostFixtureData;
}

export interface ParticipantFixture<TParticipant extends WinGameParticipant> extends WinGameFixture {
    participants: TParticipant[];
}

export type WinGameFixturePreview = Omit<WinGameFixture, 'participants' | 'optionMarkets'>;

function getFixtureId(compoundFixtureId: string): string {
    return compoundFixtureId.split(':').pop() ?? compoundFixtureId;
}

function mapParticipant(input: Participant): WinGameParticipant {
    return {
        ...input,
        id: input.id ?? 0,
    };
}

function createWinGameParticipant(input: Participant): WinGameParticipant {
    return mapParticipant(input);
}

export function createParticipantWithTimeFormData(input: Participant, timeform?: TimeformFixtureData): WinGameParticipant {
    const result = mapParticipant(input);

    if (timeform && input.id) {
        const timeformInfo = timeform.horses[input.id];

        if (timeformInfo) {
            result.timeformInfo = { ...timeformInfo };
        }
    }

    return result;
}

export function createParticipantWithRacingPostData(input: Participant, racingPost?: RacingPostFixtureData): WinGameParticipant {
    const result = mapParticipant(input);
    if (racingPost?.horses && input.id) {
        const horseInfo = racingPost.horses[input.id];
        const greyhoundInfo = racingPost.greyhounds[input.id];

        result.racingPostInfo = {};

        if (horseInfo) {
            result.racingPostInfo.horseData = { ...horseInfo, hasTopStarRating: false };
        }

        if (greyhoundInfo) {
            result.racingPostInfo.greyhoundsData = { ...greyhoundInfo };
        }
    }

    return result;
}

export function toWinGameFixture(fixture: Fixture): WinGameFixture {
    return {
        ...fixture,
        fixtureId: getFixtureId(fixture.id),
        participants: (fixture.participants || []).map((p) => createWinGameParticipant(p)),
        optionGroups: [],
        optionSets: [],
        isOptionBet: hasOptionMarkets(fixture.optionMarkets),
    };
}

export function toHorseRacingTimeFormFixture(horseRacingEntity: RaceEntity): WinGameFixture {
    const timeform: TimeformFixtureData = horseRacingEntity.timeform || { horses: {} };
    const fixture = horseRacingEntity.fixture;

    return {
        ...fixture,
        fixtureId: getFixtureId(fixture.id),
        participants: (fixture.participants || []).map((_) => createParticipantWithTimeFormData(_, timeform)),
        timeform,
        optionGroups: [],
        optionSets: [],
        isOptionBet: hasOptionMarkets(fixture.optionMarkets),
    };
}

export function toRacingPostFixture(horseRacingEntity: RaceEntity): WinGameFixture {
    const racingPost: RacingPostFixtureData = horseRacingEntity.racingPost ?? { horses: {}, greyhounds: {} };
    const fixture = horseRacingEntity.fixture;

    return {
        ...fixture,
        fixtureId: getFixtureId(fixture.id),
        participants: (fixture.participants || []).map((_) => createParticipantWithRacingPostData(_, racingPost)),
        racingpost: racingPost,
        optionGroups: [],
        optionSets: [],
        isOptionBet: hasOptionMarkets(fixture.optionMarkets),
    };
}

export function toRacingFixturePreview(fixture: Fixture): WinGameFixturePreview {
    return toWinGameFixture(fixture);
}

function hasOptionMarkets(optionMarkets: OptionMarket[]): boolean {
    return !!optionMarkets?.length;
}
